import React from "react"
import PropTypes from "prop-types"
import { Link as RouterLink } from "react-router-dom"
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tooltip,
  Skeleton,
  IconButton,
} from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"

import { COLORS } from "./constants/colors"
import OverviewMetricIcon, {
  OVERVIEW_METRIC_ICON_ACCENTS,
  OVERVIEW_METRIC_ICON_PRESETS,
} from "./OverviewMetricIcon"

export const OVERVIEW_CARD_MIN_HEIGHT_PX = 135
export const OVERVIEW_CHART_CARD_MIN_HEIGHT_PX = 380

export const overviewCardSurfaceSx = {
  borderRadius: 2,
  background: COLORS.cardBackground,
  boxShadow: "none",
  border: "1px solid #f0f0f0",
  minHeight: OVERVIEW_CARD_MIN_HEIGHT_PX,
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  backgroundImage: "none",
}

export const overviewCardContentSx = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "&:last-child": { pb: 2 },
}

export const overviewCardHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2,
  flexShrink: 0,
  color: COLORS.text,
}

export const overviewCardTitleSx = {
  fontWeight: 500,
  fontSize: 16,
  color: COLORS.text,
}

export const overviewCardValueSx = {
  fontSize: 26,
  lineHeight: 1.05,
  fontWeight: 600,
  color: COLORS.text,
}

export const overviewCardInfoIconSx = {
  fontSize: 22,
  cursor: "pointer",
  color: "#9ca3af",
}

export const overviewCardHelperTextSx = {
  color: "#6b7280",
  mt: 0.75,
}

export const overviewCardMetricHeaderSx = {
  ...overviewCardHeaderSx,
  mb: 1,
}

export function OverviewCardTitleRow({
  title,
  infoText,
  loading = false,
  sx,
  headerActionHref,
  headerActionTooltip,
}) {
  const hasTitle = title != null && title !== ""
  const showInfoIcon = Boolean(infoText)
  const showHeaderLink =
    Boolean(headerActionHref) && !loading && Boolean(headerActionTooltip)

  if (!hasTitle && !loading) {
    return null
  }

  const rightActions = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 0.25,
        flexShrink: 0,
        ml: 1,
      }}
    >
      {showHeaderLink ? (
        <Tooltip title={headerActionTooltip} placement="top" arrow>
          <IconButton
            component={RouterLink}
            to={headerActionHref}
            size="small"
            aria-label={headerActionTooltip}
            sx={{
              color: "#6b7280",
              p: 0.25,
              "&:hover": {
                backgroundColor: "rgba(17, 24, 39, 0.08)",
                color: "#374151",
              },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {showInfoIcon && !loading ? (
        <Tooltip title={infoText} placement="top" arrow>
          <InfoOutlinedIcon sx={overviewCardInfoIconSx} />
        </Tooltip>
      ) : null}
    </Box>
  )

  return (
    <Box sx={{ ...overviewCardMetricHeaderSx, ...sx }}>
      {loading ? (
        <>
          <Skeleton variant="text" width="42%" height={28} />
          {showInfoIcon ? (
            <Skeleton variant="circular" width={22} height={22} />
          ) : null}
        </>
      ) : (
        <>
          <Typography sx={{ ...overviewCardTitleSx, flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          {showInfoIcon || showHeaderLink ? rightActions : null}
        </>
      )}
    </Box>
  )
}

OverviewCardTitleRow.propTypes = {
  title: PropTypes.node,
  infoText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  loading: PropTypes.bool,
  sx: PropTypes.object,
  headerActionHref: PropTypes.string,
  headerActionTooltip: PropTypes.string,
}

OverviewCardTitleRow.defaultProps = {
  title: null,
  infoText: undefined,
  loading: false,
  sx: undefined,
  headerActionHref: undefined,
  headerActionTooltip: undefined,
}

function OverviewCard({
  title,
  infoText,
  children,
  sx,
  cardContentSx,
  loading = false,
  iconPreset,
  Icon,
  iconAccent,
  footer,
  headerActionHref,
  headerActionTooltip,
}) {
  const hasTitle = title != null && title !== ""
  const iconConfig = iconPreset
    ? OVERVIEW_METRIC_ICON_PRESETS[iconPreset]
    : Icon
      ? { Icon, accent: iconAccent }
      : null
  const useIconLayout = Boolean(iconConfig)
  const cardSx = {
    ...overviewCardSurfaceSx,
    ...sx,
  }

  let body = children

  if (useIconLayout) {
    body = (
      <Box display="flex" alignItems="flex-start" gap={2}>
        {loading ? (
          <Skeleton variant="rounded" width={44} height={44} />
        ) : (
          <OverviewMetricIcon
            Icon={iconConfig.Icon}
            accent={iconConfig.accent}
          />
        )}
        <Box flex={1} minWidth={0}>
          <OverviewCardTitleRow
            title={title}
            infoText={infoText}
            loading={loading}
            headerActionHref={headerActionHref}
            headerActionTooltip={headerActionTooltip}
          />
          {loading ? (
            <>
              <Skeleton variant="text" width="58%" height={34} />
              <Skeleton
                variant="text"
                width="72%"
                height={20}
                sx={{ mt: 0.75 }}
              />
            </>
          ) : (
            <>
              {children}
              {footer}
            </>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={{ ...overviewCardContentSx, ...cardContentSx }}>
        {!useIconLayout && hasTitle ? (
          <OverviewCardTitleRow
            title={title}
            infoText={infoText}
            loading={loading}
            sx={overviewCardHeaderSx}
            headerActionHref={headerActionHref}
            headerActionTooltip={headerActionTooltip}
          />
        ) : null}
        {body}
      </CardContent>
    </Card>
  )
}

OverviewCard.propTypes = {
  title: PropTypes.node,
  infoText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  sx: PropTypes.object,
  cardContentSx: PropTypes.object,
  loading: PropTypes.bool,
  iconPreset: PropTypes.oneOf(Object.keys(OVERVIEW_METRIC_ICON_PRESETS)),
  Icon: PropTypes.elementType,
  iconAccent: PropTypes.oneOf(OVERVIEW_METRIC_ICON_ACCENTS),
  footer: PropTypes.node,
  headerActionHref: PropTypes.string,
  headerActionTooltip: PropTypes.string,
}

OverviewCard.defaultProps = {
  title: null,
  infoText: undefined,
  sx: undefined,
  cardContentSx: undefined,
  loading: false,
  footer: null,
  headerActionHref: undefined,
  headerActionTooltip: undefined,
}

export default OverviewCard
